import * as THREE from 'three';
import { CHAR_R } from '../config.js';
import { surfaceRadius } from './planet.js';

// Registry of cylindrical colliders: { pos (world), up (unit normal), r, h }.
// Populated during prop placement; queried by the movement system.
export const colliders = [];

const _rel   = new THREE.Vector3();
const _horiz = new THREE.Vector3();

// Capped-cylinder sphere collision. Returns true if a character sphere
// (radius CHAR_R) centred at `worldPos` would intersect any collider.
// Handles both the lateral wall and the top cap (so you can't horizontally
// enter the top of a tree from above).
export function isBlocked(worldPos) {
  for (let i = 0; i < colliders.length; i++) {
    const c = colliders[i];
    _rel.copy(worldPos).sub(c.pos);
    const vert = _rel.dot(c.up);
    if (vert < -CHAR_R || vert > c.h + CHAR_R) continue;

    const horiz2 = _rel.lengthSq() - vert * vert;
    if (vert > c.h) {
      // Top cap: distance from sphere centre to the rim / disc.
      const dHoriz = Math.max(0, Math.sqrt(horiz2) - c.r);
      const dVert = vert - c.h;
      if (dHoriz * dHoriz + dVert * dVert < CHAR_R * CHAR_R) return true;
    } else {
      // Lateral wall: pure horizontal check.
      const minR = c.r + CHAR_R;
      if (horiz2 < minR * minR) return true;
    }
  }
  return false;
}

// Safety net: if the character somehow ends up inside a collider (jumped in,
// tunnelled through), push them horizontally out. Mutates `charPos`.
// Runs a few passes because escaping one collider can push into another.
const _worldPos = new THREE.Vector3();
const _target   = new THREE.Vector3();
export function ejectFromColliders(charPos, altitude, fallbackPushDir) {
  for (let pass = 0; pass < 4; pass++) {
    _worldPos.copy(charPos).multiplyScalar(surfaceRadius(charPos) + altitude);
    let moved = false;
    for (let i = 0; i < colliders.length; i++) {
      const c = colliders[i];
      _rel.copy(_worldPos).sub(c.pos);
      const vert = _rel.dot(c.up);
      if (vert < -CHAR_R || vert > c.h) continue;    // outside cylinder height

      _horiz.copy(_rel).addScaledVector(c.up, -vert);
      const horizDist = _horiz.length();
      const minR = c.r + CHAR_R;
      if (horizDist >= minR) continue;                // not laterally overlapping

      if (horizDist < 1e-5) _horiz.copy(fallbackPushDir);
      else _horiz.multiplyScalar(1 / horizDist);

      const clampedVert = Math.max(0, vert);
      _target.copy(c.pos)
        .addScaledVector(c.up, clampedVert)
        .addScaledVector(_horiz, minR + 0.02);
      charPos.copy(_target).normalize();
      moved = true;
    }
    if (!moved) break;
  }
}
